import { createSelector, createStructuredSelector } from 'reselect'
import {
  isMobileSelector,
  dimensionsSelector,
  usedFreeTrialSelector,
  recaptchaSelector,
  loadingSelector
} from 'reduxModules/app/selectors'
import { LIFETIME_PLAN_ID } from 'reduxModules/subscriptions'
import { promoDiscountForPlan } from 'reduxModules/utils/promo'
import planOffersTrial from 'reduxModules/utils/planTrial'
import _ from 'lodash'

 
export const tokenSelector = (state) => state.auth.token
export const isLoggedInSelector = (state) => !!tokenSelector(state)
export const userIdSelector = (state) => state.auth.id
export const authInfoSelector = (state) => state.auth
export const subscriptionSelector = (state) => state.auth.subscription
// The backend reports lifetime differently depending on how the membership
// was granted: checkout purchases set purchaseType, but memberships granted
// through other paths only carry the plan name/id. Check all three so guards
// never treat a lifetime member as upgradable.
export const isLifetimeSubscription = (sub) =>
  Boolean(
    sub &&
      (sub.purchaseType === 'lifetime' ||
        (typeof sub.plan === 'string' &&
          sub.plan.toLowerCase() === 'lifetime') ||
        sub.planId === LIFETIME_PLAN_ID)
  )
export const isLifetimeSelector = (state) =>
  isLifetimeSubscription(state.auth?.subscription)
export const hasPremiumAccessSelector = createSelector(
  [subscriptionSelector],
  (sub) => Boolean(sub && (isLifetimeSubscription(sub) || sub.rank >= 2))
)
export const loggedInFromEmailSelector = (state) => state.auth.loggedInFromEmail
export const profileIsOpenSelector = (state) => state.user.profileIsOpen
export const retentionDialogIsOpenSelector = (state) =>
  state.user.retentionDialogIsOpen
export const retentionDialogIsDisabledSelector = (state) =>
  state.user.retentionDialogIsDisabled
export const retentionCancelFeedbackSelector = (state) =>
  state.user.retentionCancelFeedback
export const authInfoReselector = createSelector(
  [
    authInfoSelector,
    isMobileSelector,
    usedFreeTrialSelector,
    profileIsOpenSelector,
    retentionDialogIsOpenSelector,
    retentionDialogIsDisabledSelector,
    retentionCancelFeedbackSelector
  ],
  (
    authInfo,
    isMobile,
    usedFreeTrial,
    profileIsOpen,
    retentionDialogIsOpen,
    retentionDialogIsDisabled,
    retentionCancelFeedback
  ) => ({
    canGiftSubscription: authInfo.canGiftSubscription,
    couldGiftSubscription: authInfo.couldGiftSubscription,
    email: authInfo.email,
    firstName: authInfo.firstName,
    lastName: authInfo.lastName,
    subscription: authInfo.subscription,
    role: authInfo.role,
    usedFreeTrial,
    isMobile,
    createdByProxy: authInfo.createdByProxy,
    profileIsOpen,
    retentionDialogIsOpen,
    retentionDialogIsDisabled,
    retentionCancelFeedback
  })
)

export const globalPromoSelector = (state) => state.app.globalPromo

export const isGlobalPromoEnabledSelector = (state) =>
  Boolean(state.app.globalPromo.codename && state.app.globalPromo.codename.length > 0)

export const canGiftSubscriptionSelector = (state) =>
  state.auth.canGiftSubscription

export const subscriptionReselector = createStructuredSelector({
  subscription: subscriptionSelector,
  isMobile: isMobileSelector,
  dimensions: dimensionsSelector,
  canGiftSubscription: canGiftSubscriptionSelector
})

// SPEC-063 FR-3: the checkout sign-up box must not promise a saving on a plan
// the promo cannot discount. Outside a plan selection there is nothing to
// contradict, so the promo copy stands.
export const promoAppliesToSelectedPlanSelector = (state) => {
  const plan = state.checkout?.planSelected
  if (!plan) return true
  return promoDiscountForPlan(state.app.globalPromo, plan) > 0
}

// SPEC-064: the sign-up headline must not promise a trial on a plan without one
export const selectedPlanOffersTrialSelector = (state) =>
  planOffersTrial(state.checkout?.planSelected)

export const registerBoxSelector = createStructuredSelector({
  globalPromo: globalPromoSelector,
  isGlobalPromoEnabled: isGlobalPromoEnabledSelector,
  promoAppliesToSelectedPlan: promoAppliesToSelectedPlanSelector,
  planOffersTrial: selectedPlanOffersTrialSelector,
  recaptcha: recaptchaSelector
})

const currentlyWatching = (state) => state.home.currentlyWatching
export const watchHistorySelector = createStructuredSelector({
  currentlyWatching,
  isLoading: loadingSelector
})

// SPEC-041: the /login/otp page. The flow itself (email, trigger, return
// path) travels in route state, never in this persisted slice.
export const otpLoginSelector = createStructuredSelector({
  recaptcha: recaptchaSelector,
  isMobile: isMobileSelector
})
